import { forwardRef, createElement, useRef } from 'react';
import { useTruncationTooltip } from '../../hooks/useTruncationTooltip';
import { Icon } from '../Icon';
import { useDesignTokens, renderInlineCssVars } from '../../theme';
import { getLogger } from '@sas/nova-core/logging';
import { HeadingProps, HeadingVariant } from '.';
import { setRef } from '../../util/ref-util';
import { useGetDirection } from '../../locale/useBidiFormatter';
import cx from 'classnames';
import cn from './Heading.css.json';
import './Heading.css';

const logger = getLogger('Heading');

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(props, ref) {
  const {
    className,
    style,
    level,
    variant,
    children,
    wrapText,
    icon,
    focusable,
    testId,
    disabled,
    dir,
    textProps,
    ...baseHeadingProps
  } = props;

  const dt = useDesignTokens();

  let headingTokens = {
    fg: disabled ? dt.stateStandardNova.disabled.fg : dt.c.colorTextDefault,
    focus: 'var(--focus-light-bg)',
  };

  if (dt.themeVariant === 'light' && variant === HeadingVariant.PrimaryLight) {
    headingTokens = {
      fg: disabled ? dt.statePrimaryNova.disabled.fg : dt.c.colorTextInverse,
      focus: 'var(--focus-dark-bg)',
    };
  }

  const headingStyle = renderInlineCssVars(
    {
      ...headingTokens,
      'font-weight-default': dt.fontNova.weight.default,
      'line-height': dt.fontNova.lineHeight,
      'font-size': dt.fontNova.size[`h${level}` as keyof typeof dt.fontNova.size],
    },
    'heading'
  );
  const textRef = useRef<HTMLSpanElement>(null);
  const setTruncationRef = useTruncationTooltip(undefined, textRef);
  const direction = useGetDirection(children);

  if (![1, 2, 3, 4, 5, 6].includes(level)) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.error('Heading level must be an integer between 1 and 6');
    return null;
  }
  return createElement(
    `h${level}`,
    {
      ...baseHeadingProps,
      className: cx(cn.heading, className, !wrapText && cn.nowrap),
      style: { ...headingStyle, ...style },
      tabIndex: focusable ? 0 : undefined,
      'aria-disabled': disabled ? true : undefined,
      'data-testid': testId,
      ref: (e: HTMLHeadingElement) => {
        setRef(ref, e);
        if (!wrapText) {
          setTruncationRef(e);
        }
      },
    },
    icon ? (
      <Icon
        {...icon}
        className={cx(cn.icon, !children ? cn.iconOnly : undefined)}
        disabled={disabled}
      />
    ) : undefined,
    children ? (
      <span
        {...textProps}
        className={cx(cn.text, textProps?.className)}
        ref={textRef}
        dir={textProps?.dir ?? direction}
      >
        {children}
      </span>
    ) : undefined
  );
});

export default Heading;
